(() => {
  'use strict';
  if (window.StudioHeroVideo) return;
  const roots = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const autoAllowed = () => !reduced.matches && document.body.dataset.motionEnabled !== 'false';
  const wantsPlay = state => state.visible && !document.hidden && !state.failed && !state.blocked && autoAllowed();

  function load(state) {
    if (state.loaded) return;
    state.video.muted = true;
    state.video.defaultMuted = true;
    state.video.preload = 'metadata';
    state.video.src = state.video.dataset.src;
    state.loaded = true;
    state.video.load();
  }

  function sync(state) {
    const play = wantsPlay(state);
    state.video.autoplay = play;
    if (!play) { state.video.pause(); return; }
    load(state);
    if (state.pending || !state.video.paused) return;
    state.pending = true;
    Promise.resolve(state.video.play()).catch(error => {
      if (!state.root.isConnected || !wantsPlay(state) || error.name === 'AbortError') return;
      state.blocked = true;
      state.root.dataset.videoFailed = 'true';
      state.status.textContent = state.status.dataset.errorLabel;
    }).finally(() => {
      state.pending = false;
      if (state.root.isConnected && wantsPlay(state) && state.video.paused) sync(state);
    });
  }

  const visibility = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = roots.get(entry.target.closest('[data-hero-video-root]'));
      if (state) { state.visible = entry.isIntersecting; sync(state); }
    }
  }, { threshold: 0, rootMargin: '0px' });

  function init(scope = document) {
    const elements = [...scope.querySelectorAll('[data-hero-video-root]')];
    if (scope.matches?.('[data-hero-video-root]')) elements.unshift(scope);
    for (const root of elements) {
      if (roots.has(root)) continue;
      const video = root.querySelector('[data-hero-video]');
      if (!video?.dataset.src) continue;
      const lifecycle = new AbortController();
      const media = root.querySelector('.studio-hero-video-media');
      const status = root.querySelector('[data-hero-video-status]');
      const state = { root, video, media, status, lifecycle, visible: false, loaded: false, failed: false, blocked: false, pending: false };
      roots.set(root, state);
      video.muted = true;
      video.defaultMuted = true;
      const listen = (name, action) => video.addEventListener(name, action, { signal: lifecycle.signal });
      listen('loadeddata', () => { root.dataset.videoReady = 'true'; });
      listen('playing', () => { root.dataset.videoReady = 'true'; root.dataset.videoFailed = 'false'; status.textContent = ''; });
      listen('error', () => {
        state.failed = true;
        root.dataset.videoFailed = 'true';
        video.autoplay = false;
        video.pause();
        status.textContent = status.dataset.errorLabel;
      });
      visibility.observe(media);
    }
    measureHeader();
  }

  function release(root) {
    const state = roots.get(root);
    if (!state) return;
    state.lifecycle.abort();
    state.visible = false;
    state.video.autoplay = false;
    visibility.unobserve(state.media);
    state.video.pause();
    roots.delete(root);
  }

  function measureHeader() {
    if (scrollY > 3 || !roots.size) return;
    const header = document.querySelector('[data-studio-header]');
    if (header) document.documentElement.style.setProperty('--hero-header-space', Math.ceil(header.getBoundingClientRect().bottom + 10) + 'px');
  }

  const header = document.querySelector('[data-studio-header]');
  if (header) new ResizeObserver(measureHeader).observe(header);
  addEventListener('resize', measureHeader, { passive: true });
  addEventListener('scroll', measureHeader, { passive: true });
  document.addEventListener('visibilitychange', () => { for (const state of roots.values()) sync(state); });
  const policyChanged = () => {
    for (const state of roots.values()) {
      if (state.blocked) {
        state.blocked = false;
        state.root.dataset.videoFailed = 'false';
        state.status.textContent = '';
      }
      sync(state);
    }
  };
  reduced.addEventListener('change', policyChanged);
  new MutationObserver(policyChanged).observe(document.body, { attributes: true, attributeFilter: ['data-motion-enabled'] });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of roots.keys()) if (!root.isConnected || event.target.contains(root)) release(root);
  });
  window.StudioHeroVideo = { init };
  init();
})();
